import type {
  CrmAssignmentConfig,
  CrmBaseAssignment,
  CrmOwnerRule,
} from '@/types/custom-playbooks';
import type { FieldDefinition } from '@/components/smart-columns/advanced-filter-builder.component';
import { branchConditionsComplete } from '../branch-conditions';

export const BASE_OWNER_OPTIONS: {
  value: CrmBaseAssignment['mode'];
  label: string;
}[] = [
  { value: 'keep', label: 'Keep current owner (your CRM defaults)' },
  { value: 'specific', label: 'Assign one specific owner' },
  { value: 'round_robin_all', label: 'Round-robin across all owners' },
  { value: 'round_robin_selected', label: 'Round-robin across chosen owners' },
];

export const ADVANCED_OWNER_OPTIONS = [
  { value: 'account_owner', label: 'Account owner' },
  { value: 'from_field', label: 'Owner from a field' },
  { value: 'rules', label: 'Rules' },
];

export function advancedOwner(assignment?: CrmAssignmentConfig) {
  return (
    !!assignment &&
    ['account_owner', 'from_field', 'rules'].includes(assignment.mode)
  );
}

export function freshOwnerRule(): CrmOwnerRule {
  return {
    key: 'r1',
    label: '',
    conditions: { operator: 'AND', conditions: [] },
    userIds: [],
  };
}

export function orderedOwnerRules(rules: CrmOwnerRule[]): CrmOwnerRule[] {
  return rules.map((rule, index) => ({ ...rule, key: `r${index + 1}` }));
}

export function freshOwnerAssignment(
  mode: CrmAssignmentConfig['mode']
): CrmAssignmentConfig {
  if (mode === 'rules')
    return { mode, rules: [freshOwnerRule()], fallback: { mode: 'keep' } };
  if (mode === 'from_field')
    return { mode, field: '', fallback: { mode: 'keep' } };
  if (mode === 'account_owner') return { mode, fallback: { mode: 'keep' } };
  return mode === 'specific' || mode === 'round_robin_selected'
    ? { mode, userIds: [] }
    : { mode };
}

/** Declared scalar values can hold a CRM user ID; the native resolver verifies it. */
export function syncOwnerFieldOptions(
  fields: readonly FieldDefinition[],
  saved?: string
) {
  const options = fields
    .filter(
      (field) =>
        /^(crm|trigger)\./.test(field.id) &&
        !field.id.split('.').some((part) => part.startsWith('_')) &&
        ['string', 'number', 'enum'].includes(field.type)
    )
    .map((field) => ({ value: field.id, label: field.label }));
  if (saved && !options.some((option) => option.value === saved))
    options.push({ value: saved, label: `${saved} (unavailable)` });
  return options;
}

function poolComplete(assignment: CrmBaseAssignment | undefined) {
  if (
    !assignment ||
    !BASE_OWNER_OPTIONS.some((option) => option.value === assignment.mode)
  )
    return false;
  if ('fallback' in assignment) return false;
  const ids = assignment.userIds ?? [];
  const valid =
    Array.isArray(ids) &&
    ids.length <= 20 &&
    new Set(ids).size === ids.length &&
    ids.every(
      (id) => typeof id === 'string' && !!id.trim() && id.length <= 40
    ) &&
    (assignment.mode !== 'specific' || ids.length === 1);
  return (
    valid && (assignment.mode !== 'round_robin_selected' || ids.length >= 1)
  );
}

export function advancedOwnerIssues(assignment: CrmAssignmentConfig): string[] {
  const issues: string[] = [];
  if (!poolComplete(assignment.fallback))
    issues.push(
      'Pick who gets the rest. Choose a complete fallback owner or pool.'
    );
  if (
    assignment.mode === 'from_field' &&
    (typeof assignment.field !== 'string' ||
      assignment.field.length > 200 ||
      !/^(crm|trigger)\.[A-Za-z0-9_]+(?:\.[A-Za-z0-9_]+)*$/.test(
        assignment.field
      ) ||
      assignment.field.split('.').some((part) => part.startsWith('_')))
  )
    issues.push('Choose an upstream CRM user ID field.');
  if (assignment.mode === 'rules') {
    const rules = assignment.rules;
    if (!Array.isArray(rules) || rules.length < 1 || rules.length > 10)
      return [...issues, 'Add between one and ten owner rules.'];
    if (rules.some((rule) => !rule || typeof rule !== 'object'))
      return [...issues, 'Restore the saved owner rule configuration.'];
    if (
      new Set(rules.map((rule) => rule.key)).size !== rules.length ||
      rules.some((rule) => !rule.key)
    )
      issues.push('Owner rule keys must be unique.');
    for (const [index, rule] of rules.entries()) {
      const name = rule.label || `rule ${index + 1}`;
      if (!branchConditionsComplete(rule.conditions))
        issues.push(`Complete the conditions for ${name}.`);
      if (
        !poolComplete({ mode: 'round_robin_selected', userIds: rule.userIds })
      )
        issues.push(`Add between one and twenty owners to ${name}.`);
      if (
        rule.label !== undefined &&
        (typeof rule.label !== 'string' || rule.label.length > 160)
      )
        issues.push(
          `Shorten the label for rule ${index + 1} to 160 characters.`
        );
    }
  }
  return issues;
}

function baseOwnerSummary(assignment?: CrmBaseAssignment): string {
  if (!assignment) return 'pick fallback';
  if (assignment.mode === 'keep') return 'keep owner';
  if (assignment.mode === 'specific') return 'fixed owner';
  if (assignment.mode === 'round_robin_all') return 'round-robin (all)';
  return `round-robin (${assignment.userIds?.length ?? 0})`;
}

export function advancedOwnerSummary(assignment: CrmAssignmentConfig) {
  const source =
    assignment.mode === 'account_owner'
      ? 'account owner'
      : assignment.mode === 'from_field'
        ? 'owner from field'
        : `${assignment.rules?.length ?? 0} rules`;
  return `${source}, else ${baseOwnerSummary(assignment.fallback)}`;
}

/** A Sync step's owner on its card, or null when it keeps the owner (or
 *  has no choice saved, as pre-v3 graphs). */
export function syncOwnerWords(
  assignment: CrmAssignmentConfig | undefined
): string | null {
  if (!assignment || assignment.mode === 'keep') return null;
  if (advancedOwner(assignment)) return advancedOwnerSummary(assignment);
  if (assignment.mode === 'round_robin_all') return 'round-robin (all owners)';
  if (assignment.mode === 'round_robin_selected') {
    const n = assignment.userIds?.length ?? 0;
    return `round-robin (${n} owner${n === 1 ? '' : 's'})`;
  }
  return 'fixed owner';
}
